// src/components/AdminDashboard.js - Updated with Undistributed Profit Display
import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Button,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  Divider,
  ButtonGroup,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  AccountCircle,
  People,
  TrendingUp,
  AdminPanelSettings,
  Refresh,
  CheckCircle,
  Warning,
  MonetizationOn,
  ShoppingBag,
  Assessment,
  Dashboard,
  Inventory,
  ShoppingCart,
  Receipt,
  ExpandLess,
  ExpandMore,
  Menu as MenuIcon,
  Home,
  ExitToApp,
  Analytics,
  Group,
  AccountBalanceWallet,
  AccountTree,
  TrendingFlat,
  PieChart,
  CardGiftcard,
  TrendingDown,
  Savings,
  LocalOffer,
  AccountBox,
  Assignment,
  Search,
  Clear,
  VerifiedUser,
  ConfirmationNumber,
} from "@mui/icons-material";
import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { signOut } from "firebase/auth";
import { auth, db } from "../firebase/config";
import PackageManagement from "./PackageManagement";
import OrderManagement from "./OrderManagement";
import ItemManagement from "./ItemManagement";
import UserManagement from "./UserManagement";
import RoyaltyWithdrawalManagement from "./RoyaltyWithdrawalManagement";
import AutoFillWithdrawalManagement from "./AutoFillWithdrawalManagement";
import AutoFillAdminFlowDashboard from "./AutoFillAdminFlowDashboard";
import CashOutAdminFlowDashboard from "./CashOutAdminFlowDashboard";
import UltraPlusApprovalManagement from "./UltraPlusApprovalManagement";
import MembershipVoucherManagement from "./MembershipVoucherManagement";
import { ItemRequestService } from "../services/ItemRequestService";
import "../utils/referralPointsFixer"; // Browser console utility for fixing referral points
import "../utils/deleteUserAccounts"; // Browser console utility for deleting user accounts
import { brandPalette as primaryBlue } from "../config/brandTheme";

const AdminDashboard = ({ user, onSignOut, onBackToHome }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [anchorEl, setAnchorEl] = useState(null);
  const [currentView, setCurrentView] = useState("dashboard");
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [reportsExpanded, setReportsExpanded] = useState(false);
  const [itemRequests, setItemRequests] = useState([]);

  const [stats, setStats] = useState({
    totalUsers: 0,
    totalReferrals: 0,
    validReferrals: 0,
    usersWhoWereReferred: 0,
    usersWhoMadeReferrals: 0,
    duplicateReferrals: 0,
    orphanReferrals: 0,
    companyUsers: 0,
    packagePurchasers: 0,
    commissionEligibleUsers: 0,
    totalCommissionsValue: 0,
    totalProfitMargin: 0,
    totalUndistributedProfit: 0,
    totalAdminFeesCollected: 0, // NEW: 10% admin fee from commission redemptions

    totalCashbackEarned: 0,
    totalCashbackUsed: 0,
    totalCashbackBalance: 0,
    totalCommissionEarned: 0,
    totalCommissionUsed: 0,
    totalCommissionBalance: 0,
    cashbackUtilizationRate: 0,
    commissionUtilizationRate: 0,
  });
  const [users, setUsers] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [royalties, setRoyalties] = useState([]);
  const [cleanupDialog, setCleanupDialog] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupResults, setCleanupResults] = useState(null);
  const [referralSearchQuery, setReferralSearchQuery] = useState("");

  // Glassmorphism styles

  const glassStyles = {
    background: "rgba(255, 255, 255, 0.25)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.18)",
    borderRadius: "16px",
    boxShadow: "0 8px 32px rgba(11, 28, 72, 0.37)",
  };

  const cardGlassStyles = {
    background: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(15px)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "20px",
    boxShadow: "0 8px 32px rgba(11, 28, 72, 0.2)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 16px 48px rgba(11, 28, 72, 0.35)",
      background: "rgba(255, 255, 255, 0.25)",
    },
  };

  // Navigation items
  const navigationItems = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: <Dashboard />,
      description: "Overview & Statistics",
    },
    {
      id: "users",
      label: "User Management",
      icon: <People />,
      description: "All Users & Status",
    },
    {
      id: "orders",
      label: "Order Management",
      icon: <Receipt />,
      description: "Process Orders",
    },
    {
      id: "packages",
      label: "Package Management",
      icon: <ShoppingBag />,
      description: "Manage Packages",
    },
    {
      id: "items",
      label: "Item Management",
      icon: <Inventory />,
      description: "Manage Products",
    },
    {
      id: "royalty-withdrawals",
      label: "Cash Withdrawal Request",
      icon: <AccountBalanceWallet />,
      description: "Manage cash withdrawal requests",
    },
    {
      id: "auto-fill-withdrawals",
      label: "Auto Fill Withdrawals",
      icon: <AccountBalanceWallet />,
      description: "Points wallet withdrawals (Auto Fill Program)",
    },
    {
      id: "auto-fill-flow",
      label: "Auto Fill Money Flow",
      icon: <AccountTree />,
      description: "Who paid whom and why (matrix + ledger)",
    },
    {
      id: "cash-out-flow",
      label: "Cashback Matrix & Rewards",
      icon: <Savings />,
      description: "4-level COP matrix + Rewards & Retirement wallet",
    },
    {
      id: "ultra-plus-approvals",
      label: "Ultra Plus Approval",
      icon: <VerifiedUser />,
      description: "Review T&S and V-Boost profile submissions",
    },
    {
      id: "membership-vouchers",
      label: "Membership Vouchers",
      icon: <ConfirmationNumber />,
      description: "₹699 vouchers generated from Points Wallet",
    },
    {
      id: "reports",
      label: "Reports & Analytics",
      icon: <Analytics />,
      expandable: true,
      children: [
        { id: "referrals", label: "Referral Details", icon: <Group /> },
        {
          id: "commissions",
          label: "Points Summary",
          icon: <MonetizationOn />,
        },
        {
          id: "profit-breakdown",
          label: "Profit Breakdown",
          icon: <PieChart />,
        },
        {
          id: "item-requests",
          label: "Item Requests",
          icon: <Assignment />,
        },
      ],
    },
  ];

  // Handle item request status updates
  const handleUpdateRequestStatus = async (requestId, status) => {
    try {
      setLoading(true);
      setError("");
      
      const result = await ItemRequestService.updateRequestStatus(requestId, status);
      
      if (result.success) {
        setSuccess(`Request ${status} successfully`);
        fetchAdminData(); // Refresh data
      } else {
        setError(result.error || `Failed to ${status} request`);
      }
    } catch (error) {
      console.error(`Error ${status === "approved" ? "approving" : "rejecting"} request:`, error);
      setError(`Failed to ${status} request: ` + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle item request deletion
  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm("Are you sure you want to delete this request? This action cannot be undone.")) {
      return;
    }

    try {
      setLoading(true);
      setError("");
      
      const result = await ItemRequestService.deleteRequest(requestId);
      
      if (result.success) {
        setSuccess("Request deleted successfully");
        fetchAdminData(); // Refresh data
      } else {
        setError(result.error || "Failed to delete request");
      }
    } catch (error) {
      console.error("Error deleting request:", error);
      setError("Failed to delete request: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch admin data with undistributed profit calculation
  const fetchAdminData = async () => {
    try {
      setLoading(true);
      setError("");

      console.log("Fetching admin data...");

      // Fetch all users
      const usersQuery = query(
        collection(db, "users"),
        orderBy("createdAt", "desc")
      );
      const usersSnapshot = await getDocs(usersQuery);
      const usersData = usersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Fetch all referrals
      const referralsQuery = query(
        collection(db, "referrals"),
        orderBy("createdAt", "desc")
      );
      const referralsSnapshot = await getDocs(referralsQuery);
      const referralsData = referralsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Fetch all commissions
      const commissionsQuery = query(
        collection(db, "commissions"),
        orderBy("createdAt", "desc")
      );
      const commissionsSnapshot = await getDocs(commissionsQuery);
      const commissionsData = commissionsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Fetch company royalties
      const royaltiesQuery = query(
        collection(db, "companyRoyalties"),
        orderBy("createdAt", "desc")
      );
      const royaltiesSnapshot = await getDocs(royaltiesQuery);
      const royaltiesData = royaltiesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Calculate statistics
      const totalUsers = usersData.length;
      const totalReferralRecords = referralsData.length;
      
      // Create a set of all user IDs for quick lookup
      const userIds = new Set(usersData.map(u => u.uid || u.id));
      
      // Analyze referrals for duplicates and orphans
      const referredUserCounts = {};
      let orphanReferrals = 0;
      
      referralsData.forEach(ref => {
        const referredUserId = ref.referredUserId;
        // Check if the referred user exists
        if (!userIds.has(referredUserId)) {
          orphanReferrals++;
        } else {
          referredUserCounts[referredUserId] = (referredUserCounts[referredUserId] || 0) + 1;
        }
      });
      
      // Count duplicates (users with more than 1 referral record)
      let duplicateReferrals = 0;
      Object.values(referredUserCounts).forEach(count => {
        if (count > 1) {
          duplicateReferrals += (count - 1); // Count extra records as duplicates
        }
      });
      
      // Valid referrals = unique users who have exactly 1 referral record
      const validReferrals = Object.keys(referredUserCounts).length;
      
      // Users who were referred (not by "company")
      const usersWhoWereReferred = usersData.filter(
        (user) => user.referredBy && user.referredBy !== "company" && user.referredBy.trim() !== ""
      ).length;
      
      // Users who have made at least 1 referral
      const referrerIds = new Set(referralsData.map(ref => ref.referrerId).filter(Boolean));
      const usersWhoMadeReferrals = referrerIds.size;
      
      const companyUsers = usersData.filter(
        (user) => user.referredBy === "company"
      ).length;
      const packagePurchasers = usersData.filter(
        (user) => user.hasPurchasedPackage === true
      ).length;
      const commissionEligibleUsers = usersData.filter(
        (user) => user.hasPurchasedPackage === true
      ).length;
      const totalCommissionsValue = commissionsData.reduce(
        (sum, commission) => sum + (commission.commissionAmount || 0),
        0
      );
      const totalProfitMargin = royaltiesData.reduce(
        (sum, royalty) => sum + (royalty.totalProfitMargin || 0),
        0
      );

      // Calculate total undistributed profit
      const totalUndistributedProfit = royaltiesData.reduce(
        (sum, royalty) => sum + (royalty.undistributedProfit || 0),
        0
      );

      // Calculate total commission amount from royalties (not from commissions collection)
      // This ensures the profit breakdown adds up correctly
      const totalCommissionAmountFromRoyalties = royaltiesData.reduce(
        (sum, royalty) => sum + (royalty.totalCommissionAmount || 0),
        0
      );

      // Calculate Cashback & Commission Usage Statistics
      const totalCashbackEarned = usersData.reduce(
        (sum, user) => sum + (user.cashbackEarned || 0),
        0
      );
      const totalCashbackUsed = usersData.reduce(
        (sum, user) => sum + (user.cashbackUsed || 0),
        0
      );
      const totalCashbackBalance = usersData.reduce(
        (sum, user) => sum + (user.cashbackBalance || 0),
        0
      );

      // Use the same commission balance logic as ProfileSettings
      const getActualCommissionBalance = (user) => {
        const newSystemBalance = user.commissionBalance || 0;
        const oldSystemBalance = user.confirmedCommissions || 0;
        return newSystemBalance > 0 ? newSystemBalance : oldSystemBalance;
      };

      const getActualCommissionEarned = (user) => {
        const newSystemEarned = user.commissionEarned || 0;
        const oldSystemEarned = user.totalCommissions || 0;
        return newSystemEarned > 0 ? newSystemEarned : oldSystemEarned;
      };

      const totalCommissionEarned = usersData.reduce(
        (sum, user) => sum + getActualCommissionEarned(user),
        0
      );
      const totalCommissionUsed = usersData.reduce(
        (sum, user) => sum + (user.commissionUsed || 0),
        0
      );
      const totalCommissionBalance = usersData.reduce(
        (sum, user) => sum + getActualCommissionBalance(user),
        0
      );

      // Calculate utilization rates
      const cashbackUtilizationRate =
        totalCashbackEarned > 0
          ? (totalCashbackUsed / totalCashbackEarned) * 100
          : 0;
      const commissionUtilizationRate =
        totalCommissionEarned > 0
          ? (totalCommissionUsed / totalCommissionEarned) * 100
          : 0;

      // Fetch item requests
      const itemRequestsResult = await ItemRequestService.getAllItemRequests();
      if (itemRequestsResult.success) {
        setItemRequests(itemRequestsResult.data);
      } else {
        console.error("Failed to fetch item requests:", itemRequestsResult.error);
      }

      // Fetch orders to calculate total admin fees collected (10% from commission redemptions)
      const ordersQuery = query(
        collection(db, "orders"),
        orderBy("createdAt", "desc")
      );
      const ordersSnapshot = await getDocs(ordersQuery);
      const ordersData = ordersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Calculate total admin fees collected from confirmed orders
      const totalAdminFeesCollected = ordersData.reduce((sum, order) => {
        // Only count from confirmed/completed orders
        if (order.paymentStatus === "confirmed" || order.orderStatus === "confirmed" || order.adminConfirmed === true) {
          const adminFee = order.appliedDiscounts?.commissionAdminFee || 0;
          return sum + adminFee;
        }
        return sum;
      }, 0);

      console.log("Total Admin Fees Collected:", totalAdminFeesCollected);

      setUsers(usersData);
      setReferrals(referralsData);
      setCommissions(commissionsData);
      setRoyalties(royaltiesData);
      setStats({
        totalUsers,
        totalReferrals: totalReferralRecords,
        validReferrals,
        usersWhoWereReferred,
        usersWhoMadeReferrals,
        duplicateReferrals,
        orphanReferrals,
        companyUsers,
        packagePurchasers,
        commissionEligibleUsers,
        totalCommissionsValue,
        totalCommissionAmountFromRoyalties, // NEW: For accurate profit breakdown
        totalProfitMargin,
        totalUndistributedProfit,
        totalAdminFeesCollected, // NEW: Total 10% admin fees from commission redemptions

        totalCashbackEarned,
        totalCashbackUsed,
        totalCashbackBalance,
        totalCommissionEarned,
        totalCommissionUsed,
        totalCommissionBalance,
        cashbackUtilizationRate,
        commissionUtilizationRate,
      });

      console.log("Admin data loaded successfully");
      console.log("Total Undistributed Profit:", totalUndistributedProfit);
      console.log("Total Admin Fees Collected:", totalAdminFeesCollected);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      setError("Failed to fetch admin data: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Fix referral data
  const fixReferralData = async () => {
    try {
      setLoading(true);
      setError("");

      let updatedCount = 0;

      for (const referral of referrals) {
        if (!referral.referrerEmail) {
          const referrerUser = users.find((u) => u.id === referral.referrerId);
          if (referrerUser) {
            await updateDoc(doc(db, "referrals", referral.id), {
              referrerEmail: referrerUser.email,
            });
            updatedCount++;
          }
        }
      }

      if (updatedCount > 0) {
        setSuccess(`Fixed ${updatedCount} referral records`);
        fetchAdminData();
      } else {
        setSuccess("All referral records are already correct");
      }
    } catch (error) {
      setError("Failed to fix referral data: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Analyze and cleanup duplicate/orphan referrals
  const analyzeReferralData = () => {
    const userIds = new Set(users.map(u => u.uid || u.id));
    const referredUserCounts = {};
    const orphanRecords = [];
    const duplicateRecords = [];
    
    referrals.forEach(ref => {
      const referredUserId = ref.referredUserId;
      if (!userIds.has(referredUserId)) {
        orphanRecords.push(ref);
      } else {
        if (!referredUserCounts[referredUserId]) {
          referredUserCounts[referredUserId] = [];
        }
        referredUserCounts[referredUserId].push(ref);
      }
    });
    
    // Find duplicates (keep the first one, mark rest as duplicates)
    Object.entries(referredUserCounts).forEach(([userId, records]) => {
      if (records.length > 1) {
        // Sort by createdAt to keep the earliest
        records.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
          const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
          return dateA - dateB;
        });
        // Mark all but the first as duplicates
        duplicateRecords.push(...records.slice(1));
      }
    });
    
    return { orphanRecords, duplicateRecords };
  };

  const handleCleanupReferrals = async () => {
    try {
      setCleanupLoading(true);
      const { orphanRecords, duplicateRecords } = analyzeReferralData();
      
      let deletedOrphans = 0;
      let deletedDuplicates = 0;
      
      // Delete orphan records
      for (const record of orphanRecords) {
        try {
          await deleteDoc(doc(db, "referrals", record.id));
          deletedOrphans++;
        } catch (err) {
          console.error("Failed to delete orphan referral:", record.id, err);
        }
      }
      
      // Delete duplicate records
      for (const record of duplicateRecords) {
        try {
          await deleteDoc(doc(db, "referrals", record.id));
          deletedDuplicates++;
        } catch (err) {
          console.error("Failed to delete duplicate referral:", record.id, err);
        }
      }
      
      setCleanupResults({
        deletedOrphans,
        deletedDuplicates,
        total: deletedOrphans + deletedDuplicates
      });
      
      // Refresh data
      await fetchAdminData();
      setSuccess(`Cleanup complete! Removed ${deletedOrphans} orphan records and ${deletedDuplicates} duplicate records.`);
    } catch (error) {
      setError("Cleanup failed: " + error.message);
    } finally {
      setCleanupLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminData();
  }, []);

  useEffect(() => {
    if (isMobile) {
      setDrawerOpen(false);
    }
  }, [isMobile]);

  const handleMenu = (event) => {
    // Since we're already on AdminDashboard, clicking profile icon should go back to home
    if (onBackToHome) {
      onBackToHome();
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      onSignOut();
      handleClose();
    } catch (error) {
      setError("Failed to sign out: " + error.message);
    }
  };

  const handleNavigation = (viewId) => {
    setCurrentView(viewId);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(price);
  };

  const getCommissionEligibilityChip = (user) => {
    if (user.hasPurchasedPackage) {
      return (
        <Tooltip title="User has purchased a package and can earn commissions">
          <Chip
            label="Points Eligible"
            size="small"
            color="success"
            icon={<CheckCircle />}
          />
        </Tooltip>
      );
    } else {
      return (
        <Tooltip title="User must purchase a package to earn commissions">
          <Chip
            label="Package Required"
            size="small"
            color="warning"
            icon={<Warning />}
          />
        </Tooltip>
      );
    }
  };

  const getUserTypeChip = (user) => {
    if (user.hasPurchasedPackage) {
      return <Chip label="Package Buyer" size="small" color="primary" />;
    } else if (user.registrationFee?.confirmedByAdmin) {
      return <Chip label="Registered" size="small" color="info" />;
    } else if (user.registrationFee?.paid) {
      return <Chip label="Payment Pending" size="small" color="warning" />;
    } else {
      return <Chip label="New User" size="small" color="default" />;
    }
  };

  // Statistics Cards Component with undistributed profit
  const StatisticsCards = () => (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid item xs={12} sm={6} md={4}>
        <Card sx={{ ...cardGlassStyles, bgcolor: "rgba(0, 114, 206, 0.1)" }}>
          <CardContent sx={{ textAlign: "center", p: 3 }}>
            <People sx={{ fontSize: 50, color: primaryBlue[600], mb: 2 }} />
            <Typography
              variant="h3"
              sx={{ color: primaryBlue[700], fontWeight: 700, mb: 1 }}
            >
              {stats.totalUsers}
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: primaryBlue[600], fontWeight: 600 }}
            >
              Total Users
            </Typography>
            <Typography variant="body2" sx={{ color: primaryBlue[500], mt: 1 }}>
              Registered members
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <Card sx={{ ...cardGlassStyles, bgcolor: "rgba(76, 175, 80, 0.1)" }}>
          <CardContent sx={{ textAlign: "center", p: 3 }}>
            <ShoppingBag sx={{ fontSize: 50, color: "#00BFFF", mb: 2 }} />
            <Typography
              variant="h3"
              sx={{ color: "#2e7d32", fontWeight: 700, mb: 1 }}
            >
              {stats.packagePurchasers}
            </Typography>
            <Typography variant="h6" sx={{ color: "#2e7d32", fontWeight: 600 }}>
              Package Buyers
            </Typography>
            <Typography variant="body2" sx={{ color: "#388e3c", mt: 1 }}>
              Supreme customers
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <Card sx={{ ...cardGlassStyles, bgcolor: "rgba(255, 152, 0, 0.1)" }}>
          <CardContent sx={{ textAlign: "center", p: 3 }}>
            <MonetizationOn sx={{ fontSize: 50, color: "#D4AF37", mb: 2 }} />
            <Typography
              variant="h3"
              sx={{ color: "#f57c00", fontWeight: 700, mb: 1 }}
            >
              {stats.commissionEligibleUsers}
            </Typography>
            <Typography variant="h6" sx={{ color: "#f57c00", fontWeight: 600 }}>
              Points Eligible
            </Typography>
            <Typography variant="body2" sx={{ color: "#D4AF37", mt: 1 }}>
              Can earn Points
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <Card sx={{ ...cardGlassStyles, bgcolor: "rgba(156, 39, 176, 0.1)" }}>
          <CardContent sx={{ textAlign: "center", p: 3 }}>
            <TrendingUp sx={{ fontSize: 50, color: "#1E40AF", mb: 2 }} />
            <Typography
              variant="h3"
              sx={{ color: "#7b1fa2", fontWeight: 700, mb: 1 }}
            >
              {stats.usersWhoWereReferred}
            </Typography>
            <Typography variant="h6" sx={{ color: "#7b1fa2", fontWeight: 600 }}>
              Referred Users
            </Typography>
            <Typography variant="body2" sx={{ color: "#1E40AF", mt: 1 }}>
              Users who joined via referral
            </Typography>
            {(stats.duplicateReferrals > 0 || stats.orphanReferrals > 0) && (
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={() => setCleanupDialog(true)}
                sx={{ mt: 1, fontSize: "0.7rem" }}
              >
                ⚠️ {stats.duplicateReferrals + stats.orphanReferrals} issues found
              </Button>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <Card sx={{ ...cardGlassStyles, bgcolor: "rgba(244, 67, 54, 0.1)" }}>
          <CardContent sx={{ textAlign: "center", p: 3 }}>
            <MonetizationOn sx={{ fontSize: 50, color: "#001A4D", mb: 2 }} />
            <Typography
              variant="h3"
              sx={{ color: "#d32f2f", fontWeight: 700, mb: 1 }}
            >
              {formatPrice(stats.totalCommissionsValue)}
            </Typography>
            <Typography variant="h6" sx={{ color: "#d32f2f", fontWeight: 600 }}>
              Total Points
            </Typography>
            <Typography variant="body2" sx={{ color: "#001A4D", mt: 1 }}>
              Distributed to referrers
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* NEW: Admin Fees Collected Card (10% from commission redemptions) */}
      <Grid item xs={12} sm={6} md={4}>
        <Card sx={{ ...cardGlassStyles, bgcolor: "rgba(255, 152, 0, 0.1)" }}>
          <CardContent sx={{ textAlign: "center", p: 3 }}>
            <MonetizationOn sx={{ fontSize: 50, color: "#D4AF37", mb: 2 }} />
            <Typography
              variant="h3"
              sx={{ color: "#f57c00", fontWeight: 700, mb: 1 }}
            >
              {formatPrice(stats.totalAdminFeesCollected)}
            </Typography>
            <Typography variant="h6" sx={{ color: "#f57c00", fontWeight: 600 }}>
              Admin Fees Collected
            </Typography>
            <Typography variant="body2" sx={{ color: "#D4AF37", mt: 1 }}>
              10% fee from Points redemptions
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* NEW: Undistributed Profit Card */}
      <Grid item xs={12} sm={6} md={4}>
        <Card sx={{ ...cardGlassStyles, bgcolor: "rgba(103, 58, 183, 0.1)" }}>
          <CardContent sx={{ textAlign: "center", p: 3 }}>
            <TrendingFlat sx={{ fontSize: 50, color: "#002366", mb: 2 }} />
            <Typography
              variant="h3"
              sx={{ color: "#512da8", fontWeight: 700, mb: 1 }}
            >
              {formatPrice(stats.totalUndistributedProfit)}
            </Typography>
            <Typography variant="h6" sx={{ color: "#512da8", fontWeight: 600 }}>
              Undistributed Profit
            </Typography>
            <Typography variant="body2" sx={{ color: "#002366", mt: 1 }}>
              From incomplete referral chains
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* NEW: Total Profit Overview Card */}
      <Grid item xs={12} sm={6} md={4}>
        <Card sx={{ ...cardGlassStyles, bgcolor: "rgba(76, 175, 80, 0.1)" }}>
          <CardContent sx={{ textAlign: "center", p: 3 }}>
            <Assessment sx={{ fontSize: 50, color: "#00BFFF", mb: 2 }} />
            <Typography
              variant="h3"
              sx={{ color: "#2e7d32", fontWeight: 700, mb: 1 }}
            >
              {formatPrice(stats.totalProfitMargin)}
            </Typography>
            <Typography variant="h6" sx={{ color: "#2e7d32", fontWeight: 600 }}>
              Total Profit Margin
            </Typography>
            <Typography variant="body2" sx={{ color: "#388e3c", mt: 1 }}>
              From all confirmed orders
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Enhanced Profit Breakdown Card */}
      <Grid item xs={12}>
        <Card
          sx={{
            ...cardGlassStyles,
            bgcolor: "rgba(76, 175, 80, 0.05)",
            border: "2px solid rgba(76, 175, 80, 0.2)",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Assessment sx={{ fontSize: 40, color: "#00BFFF", mr: 2 }} />
              <Box>
                <Typography
                  variant="h5"
                  sx={{ color: "#2e7d32", fontWeight: 700 }}
                >
                  Complete Profit Distribution Analysis
                </Typography>
                <Typography variant="body1" sx={{ color: "#388e3c" }}>
                  Total Profit Margins: {formatPrice(stats.totalProfitMargin)}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "rgba(244, 67, 54, 0.1)",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ color: "#d32f2f", fontWeight: 600, mb: 1 }}
                  >
                    Points Payouts
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{ color: "#001A4D", fontWeight: 700 }}
                  >
                    {formatPrice(stats.totalCommissionsValue)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#d32f2f", mt: 1 }}>
                    {stats.totalProfitMargin > 0
                      ? `${(
                          (stats.totalCommissionsValue /
                            stats.totalProfitMargin) *
                          100
                        ).toFixed(1)}%`
                      : "0%"}{" "}
                    of total profit
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "rgba(103, 58, 183, 0.1)",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ color: "#512da8", fontWeight: 600, mb: 1 }}
                  >
                    Undistributed Profit
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{ color: "#002366", fontWeight: 700 }}
                  >
                    {formatPrice(stats.totalUndistributedProfit)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#002366", mt: 1 }}>
                    {stats.totalProfitMargin > 0
                      ? `${(
                          (stats.totalUndistributedProfit /
                            stats.totalProfitMargin) *
                          100
                        ).toFixed(1)}%`
                      : "0%"}{" "}
                    remaining profit
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "rgba(76, 175, 80, 0.1)",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ color: "#2e7d32", fontWeight: 600, mb: 1 }}
                  >
                    Total Accounted
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{ color: "#00BFFF", fontWeight: 700 }}
                  >
                    {formatPrice(
                      stats.totalCommissionAmountFromRoyalties +
                        stats.totalUndistributedProfit
                    )}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#388e3c", mt: 1 }}>
                    Should equal total profit
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Box
              sx={{
                mt: 3,
                p: 2,
                bgcolor: "rgba(255, 255, 255, 0.5)",
                borderRadius: 2,
              }}
            >
              <Typography
                variant="body1"
                sx={{ color: "#2e7d32", fontWeight: 600, mb: 1 }}
              >
                📊 Profit Distribution Summary:
              </Typography>
              <Typography variant="body2" sx={{ color: "#388e3c" }}>
                • <strong>Points:</strong> Up to 100% of profit margin across 10
                levels (L1 100%, L2 25%, L3 15%, L4–L5 10%, L6–L8 5%, L9–L10 2.5%)
                <br />• <strong>Undistributed Profit:</strong> Remaining margin
                when the referral chain has fewer than 10 levels
                <br />• <strong>Note:</strong> Undistributed profit represents
                potential Points earnings that weren't distributed due to
                incomplete referral chains
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* NEW: Cashback Statistics Cards */}
      <Grid item xs={12} sm={6} md={4}>
        <Card sx={{ ...cardGlassStyles, bgcolor: "rgba(76, 175, 80, 0.1)" }}>
          <CardContent sx={{ textAlign: "center", p: 3 }}>
            <CardGiftcard sx={{ fontSize: 50, color: "#00BFFF", mb: 2 }} />
            <Typography
              variant="h3"
              sx={{ color: "#2e7d32", fontWeight: 700, mb: 1 }}
            >
              ₹{stats.totalCashbackEarned.toFixed(0)}
            </Typography>
            <Typography variant="h6" sx={{ color: "#2e7d32", fontWeight: 600 }}>
              Cashback Earned
            </Typography>
            <Typography variant="body2" sx={{ color: "#388e3c", mt: 1 }}>
              Total generated
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <Card sx={{ ...cardGlassStyles, bgcolor: "rgba(244, 67, 54, 0.1)" }}>
          <CardContent sx={{ textAlign: "center", p: 3 }}>
            <TrendingDown sx={{ fontSize: 50, color: "#001A4D", mb: 2 }} />
            <Typography
              variant="h3"
              sx={{ color: "#d32f2f", fontWeight: 700, mb: 1 }}
            >
              ₹{stats.totalCashbackUsed.toFixed(0)}
            </Typography>
            <Typography variant="h6" sx={{ color: "#d32f2f", fontWeight: 600 }}>
              Cashback Used
            </Typography>
            <Typography variant="body2" sx={{ color: "#001A4D", mt: 1 }}>
              {stats.cashbackUtilizationRate.toFixed(1)}% utilization
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <Card sx={{ ...cardGlassStyles, bgcolor: "rgba(103, 58, 183, 0.1)" }}>
          <CardContent sx={{ textAlign: "center", p: 3 }}>
            <Savings sx={{ fontSize: 50, color: "#002366", mb: 2 }} />
            <Typography
              variant="h3"
              sx={{ color: "#512da8", fontWeight: 700, mb: 1 }}
            >
              ₹{stats.totalCashbackBalance.toFixed(0)}
            </Typography>
            <Typography variant="h6" sx={{ color: "#512da8", fontWeight: 600 }}>
              Cashback Balance
            </Typography>
            <Typography variant="body2" sx={{ color: "#002366", mt: 1 }}>
              Available to users
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Points Statistics Cards */}
      <Grid item xs={12} sm={6} md={4}>
        <Card sx={{ ...cardGlassStyles, bgcolor: "rgba(0, 114, 206, 0.1)" }}>
          <CardContent sx={{ textAlign: "center", p: 3 }}>
            <LocalOffer sx={{ fontSize: 50, color: primaryBlue[600], mb: 2 }} />
            <Typography
              variant="h3"
              sx={{ color: primaryBlue[700], fontWeight: 700, mb: 1 }}
            >
              ₹{stats.totalCommissionEarned.toFixed(0)}
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: primaryBlue[600], fontWeight: 600 }}
            >
              Points Earned
            </Typography>
            <Typography variant="body2" sx={{ color: primaryBlue[500], mt: 1 }}>
              Total distributed
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <Card sx={{ ...cardGlassStyles, bgcolor: "rgba(255, 152, 0, 0.1)" }}>
          <CardContent sx={{ textAlign: "center", p: 3 }}>
            <TrendingDown sx={{ fontSize: 50, color: "#D4AF37", mb: 2 }} />
            <Typography
              variant="h3"
              sx={{ color: "#f57c00", fontWeight: 700, mb: 1 }}
            >
              ₹{stats.totalCommissionUsed.toFixed(0)}
            </Typography>
            <Typography variant="h6" sx={{ color: "#f57c00", fontWeight: 600 }}>
              Points Used
            </Typography>
            <Typography variant="body2" sx={{ color: "#D4AF37", mt: 1 }}>
              {stats.commissionUtilizationRate.toFixed(1)}% utilization
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <Card sx={{ ...cardGlassStyles, bgcolor: "rgba(156, 39, 176, 0.1)" }}>
          <CardContent sx={{ textAlign: "center", p: 3 }}>
            <AccountBox sx={{ fontSize: 50, color: "#1E40AF", mb: 2 }} />
            <Typography
              variant="h3"
              sx={{ color: "#7b1fa2", fontWeight: 700, mb: 1 }}
            >
              ₹{stats.totalCommissionBalance.toFixed(0)}
            </Typography>
            <Typography variant="h6" sx={{ color: "#7b1fa2", fontWeight: 600 }}>
              Points Balance
            </Typography>
            <Typography variant="body2" sx={{ color: "#1E40AF", mt: 1 }}>
              Available to users
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  // Navigation Drawer
  const NavigationDrawer = () => (
    <Drawer
      variant={isMobile ? "temporary" : "persistent"}
      anchor="left"
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      sx={{
        width: 280,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: 280,
          boxSizing: "border-box",
          background: `linear-gradient(180deg, ${primaryBlue[50]} 0%, ${primaryBlue[100]} 100%)`,
          backdropFilter: "blur(20px)",
          border: "none",
          borderRight: `1px solid ${primaryBlue[200]}`,
        },
      }}
    >
      <Box sx={{ p: 3, borderBottom: `1px solid ${primaryBlue[200]}` }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <AdminPanelSettings
            sx={{ fontSize: 32, color: primaryBlue[700], mr: 2 }}
          />
          <Typography
            variant="h6"
            sx={{ color: primaryBlue[800], fontWeight: 700 }}
          >
            Admin Panel
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar src={user.photoURL} sx={{ width: 32, height: 32 }}>
            <AccountCircle />
          </Avatar>
          <Box>
            <Typography
              variant="body2"
              sx={{ color: primaryBlue[700], fontWeight: 600 }}
            >
              {user.displayName?.split(" ")[0] || "Admin"}
            </Typography>
            <Typography variant="caption" sx={{ color: primaryBlue[600] }}>
              Administrator
            </Typography>
          </Box>
        </Box>
      </Box>

      <List sx={{ flexGrow: 1, p: 2 }}>
        {navigationItems.map((item) => (
          <React.Fragment key={item.id}>
            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => {
                  if (item.expandable) {
                    setReportsExpanded(!reportsExpanded);
                  } else {
                    handleNavigation(item.id);
                  }
                }}
                selected={currentView === item.id}
                sx={{
                  borderRadius: 2,
                  bgcolor:
                    currentView === item.id
                      ? "rgba(0, 114, 206, 0.1)"
                      : "transparent",
                  border:
                    currentView === item.id
                      ? `1px solid ${primaryBlue[300]}`
                      : "1px solid transparent",
                  "&:hover": {
                    bgcolor: "rgba(0, 114, 206, 0.08)",
                    border: `1px solid ${primaryBlue[200]}`,
                  },
                  transition: "all 0.2s ease-in-out",
                }}
              >
                <ListItemIcon
                  sx={{
                    color:
                      currentView === item.id
                        ? primaryBlue[700]
                        : primaryBlue[600],
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: currentView === item.id ? 600 : 400 }}
                    >
                      {item.label}
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="caption"
                      sx={{ color: primaryBlue[600] }}
                    >
                      {item.description}
                    </Typography>
                  }
                />
                {item.expandable &&
                  (reportsExpanded ? <ExpandLess /> : <ExpandMore />)}
              </ListItemButton>
            </ListItem>

            {item.expandable && (
              <Collapse in={reportsExpanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.children.map((child) => (
                    <ListItem
                      key={child.id}
                      disablePadding
                      sx={{ pl: 2, mb: 0.5 }}
                    >
                      <ListItemButton
                        onClick={() => handleNavigation(child.id)}
                        selected={currentView === child.id}
                        sx={{
                          borderRadius: 2,
                          bgcolor:
                            currentView === child.id
                              ? "rgba(0, 114, 206, 0.1)"
                              : "transparent",
                          border:
                            currentView === child.id
                              ? `1px solid ${primaryBlue[300]}`
                              : "1px solid transparent",
                          "&:hover": {
                            bgcolor: "rgba(0, 114, 206, 0.08)",
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            color:
                              currentView === child.id
                                ? primaryBlue[700]
                                : primaryBlue[500],
                            minWidth: 36,
                          }}
                        >
                          {child.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight:
                                  currentView === child.id ? 600 : 400,
                              }}
                            >
                              {child.label}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>

      <Box sx={{ p: 2, borderTop: `1px solid ${primaryBlue[200]}` }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<Home />}
          onClick={onBackToHome}
          sx={{
            mb: 1,
            borderColor: primaryBlue[300],
            color: primaryBlue[700],
            "&:hover": {
              borderColor: primaryBlue[500],
              bgcolor: "rgba(0, 114, 206, 0.08)",
            },
          }}
        >
          Back to Home
        </Button>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<ExitToApp />}
          onClick={handleSignOut}
          sx={{
            borderColor: "#001A4D",
            color: "#001A4D",
            "&:hover": {
              borderColor: "#d32f2f",
              bgcolor: "rgba(244, 67, 54, 0.08)",
            },
          }}
        >
          Sign Out
        </Button>
      </Box>
    </Drawer>
  );

  // Profit Breakdown View Component
  const ProfitBreakdownView = () => (
    <Card sx={{ ...glassStyles, bgcolor: "rgba(255, 255, 255, 0.8)" }}>
      <CardContent sx={{ p: 4 }}>
        <Typography
          variant="h5"
          sx={{ color: primaryBlue[800], fontWeight: 700, mb: 3 }}
        >
          Detailed Profit Breakdown Analysis
        </Typography>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Card
              sx={{ ...cardGlassStyles, bgcolor: "rgba(76, 175, 80, 0.1)" }}
            >
              <CardContent sx={{ textAlign: "center", p: 3 }}>
                <Assessment sx={{ fontSize: 40, color: "#00BFFF", mb: 2 }} />
                <Typography
                  variant="h5"
                  sx={{ color: "#2e7d32", fontWeight: 700 }}
                >
                  {formatPrice(stats.totalProfitMargin)}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "#2e7d32", fontWeight: 600 }}
                >
                  Total Profit Margin
                </Typography>
                <Typography variant="body2" sx={{ color: "#388e3c" }}>
                  100% of all profits
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{ ...cardGlassStyles, bgcolor: "rgba(244, 67, 54, 0.1)" }}
            >
              <CardContent sx={{ textAlign: "center", p: 3 }}>
                <MonetizationOn
                  sx={{ fontSize: 40, color: "#001A4D", mb: 2 }}
                />
                <Typography
                  variant="h5"
                  sx={{ color: "#d32f2f", fontWeight: 700 }}
                >
                  {formatPrice(stats.totalCommissionsValue)}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "#d32f2f", fontWeight: 600 }}
                >
                  Distributed Points
                </Typography>
                <Typography variant="body2" sx={{ color: "#001A4D" }}>
                  {stats.totalProfitMargin > 0
                    ? `${(
                        (stats.totalCommissionsValue /
                          stats.totalProfitMargin) *
                        100
                      ).toFixed(1)}%`
                    : "0%"}{" "}
                  of profit
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{ ...cardGlassStyles, bgcolor: "rgba(103, 58, 183, 0.1)" }}
            >
              <CardContent sx={{ textAlign: "center", p: 3 }}>
                <TrendingFlat sx={{ fontSize: 40, color: "#002366", mb: 2 }} />
                <Typography
                  variant="h5"
                  sx={{ color: "#512da8", fontWeight: 700 }}
                >
                  {formatPrice(stats.totalUndistributedProfit)}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "#512da8", fontWeight: 600 }}
                >
                  Undistributed Profit
                </Typography>
                <Typography variant="body2" sx={{ color: "#002366" }}>
                  {stats.totalProfitMargin > 0
                    ? `${(
                        (stats.totalUndistributedProfit /
                          stats.totalProfitMargin) *
                        100
                      ).toFixed(1)}%`
                    : "0%"}{" "}
                  remaining
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Detailed Breakdown Table */}
        <TableContainer
          sx={{ ...glassStyles, bgcolor: "rgba(255, 255, 255, 0.9)", mb: 4 }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, bgcolor: primaryBlue[50] }}>
                  Order ID
                </TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: primaryBlue[50] }}>
                  Buyer
                </TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: primaryBlue[50] }}>
                  Total Profit
                </TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: primaryBlue[50] }}>
                  Points Paid
                </TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: primaryBlue[50] }}>
                  Undistributed
                </TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: primaryBlue[50] }}>
                  Date
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {royalties.slice(0, 20).map((royalty) => (
                <TableRow
                  key={royalty.id}
                  sx={{ "&:hover": { bgcolor: "rgba(0, 114, 206, 0.04)" } }}
                >
                  <TableCell
                    sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
                  >
                    {royalty.orderId?.slice(-8) || "N/A"}
                  </TableCell>
                  <TableCell>{royalty.buyerName}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ color: "#00BFFF", fontWeight: 600 }}
                    >
                      {formatPrice(royalty.totalProfitMargin || 0)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ color: "#001A4D", fontWeight: 600 }}
                    >
                      {formatPrice(royalty.totalCommissionAmount || 0)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ color: "#002366", fontWeight: 600 }}
                    >
                      {formatPrice(royalty.undistributedProfit || 0)}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatDate(royalty.createdAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Insights and Recommendations */}
        <Alert
          severity="info"
          sx={{ ...glassStyles, bgcolor: "rgba(0, 114, 206, 0.1)" }}
        >
          <Typography
            variant="body1"
            sx={{ fontWeight: 600, mb: 1, color: primaryBlue[800] }}
          >
            💡 Profit Distribution Insights:
          </Typography>
          <Typography variant="body2" sx={{ color: primaryBlue[700] }}>
            • <strong>Total Undistributed:</strong>{" "}
            {formatPrice(stats.totalUndistributedProfit)} represents potential
            Points earnings that weren't distributed due to incomplete referral
            chains
            <br />• <strong>Average Distribution Rate:</strong>{" "}
            {stats.totalProfitMargin > 0
              ? `${(
                  (stats.totalCommissionsValue / stats.totalProfitMargin) *
                  100
                ).toFixed(1)}%`
              : "0%"}{" "}
            of profits are being distributed as Points
            <br />• <strong>Recommendation:</strong> Consider strategies to
            encourage longer referral chains to maximize Points distribution and
            network growth
          </Typography>
        </Alert>
      </CardContent>
    </Card>
  );

  // Main Content Renderer
  const renderMainContent = () => {
    if (loading) {
      return (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2, color: primaryBlue[700] }}>
            Loading admin data...
          </Typography>
        </Box>
      );
    }

    switch (currentView) {
      case "dashboard":
        return (
          <>
            <StatisticsCards />
            <Alert
              severity="info"
              sx={{ ...glassStyles, mb: 3, bgcolor: "rgba(0, 114, 206, 0.1)" }}
            >
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, mb: 1, color: primaryBlue[800] }}
              >
                💰 Points & Royalty System Overview
              </Typography>
              <Typography variant="body2" sx={{ color: primaryBlue[700] }}>
                <strong>Points Policy:</strong> Only users who have purchased
                packages can earn referral Points — up to 100% of profit margin
                across 10 levels (100% / 25% / 15% / 10% / 10% / 5%×3 / 2.5%×2).
                <br />
                <strong>Undistributed Profit:</strong> When referral chains are
                incomplete (fewer than 10 levels), the unused level percentages
                are tracked as undistributed profit.
              </Typography>
            </Alert>
          </>
        );

      case "users":
        return <UserManagement users={users} onRefresh={fetchAdminData} />;

      case "orders":
        return (
          <Card sx={{ ...glassStyles, bgcolor: "rgba(255, 255, 255, 0.9)" }}>
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h5"
                sx={{ color: primaryBlue[800], fontWeight: 700, mb: 3 }}
              >
                Order Management
              </Typography>
              <OrderManagement />
            </CardContent>
          </Card>
        );

      case "packages":
        return (
          <Card sx={{ ...glassStyles, bgcolor: "rgba(255, 255, 255, 0.9)" }}>
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h5"
                sx={{ color: primaryBlue[800], fontWeight: 700, mb: 3 }}
              >
                Package Management
              </Typography>
              <PackageManagement />
            </CardContent>
          </Card>
        );

      case "items":
        return (
          <Card sx={{ ...glassStyles, bgcolor: "rgba(255, 255, 255, 0.9)" }}>
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h5"
                sx={{ color: primaryBlue[800], fontWeight: 700, mb: 3 }}
              >
                Item Management
              </Typography>
              <ItemManagement />
            </CardContent>
          </Card>
        );

      case "royalty-withdrawals":
        return (
          <Card sx={{ ...glassStyles, bgcolor: "rgba(255, 255, 255, 0.9)" }}>
            <CardContent sx={{ p: 3 }}>
              <RoyaltyWithdrawalManagement />
            </CardContent>
          </Card>
        );

      case "auto-fill-withdrawals":
        return (
          <Card sx={{ ...glassStyles, bgcolor: "rgba(255, 255, 255, 0.9)" }}>
            <CardContent sx={{ p: 3 }}>
              <AutoFillWithdrawalManagement adminUserId={user?.uid} />
            </CardContent>
          </Card>
        );

      case "auto-fill-flow":
        return (
          <Card sx={{ ...glassStyles, bgcolor: "rgba(255, 255, 255, 0.9)" }}>
            <CardContent sx={{ p: 3 }}>
              <AutoFillAdminFlowDashboard />
            </CardContent>
          </Card>
        );

      case "cash-out-flow":
        return (
          <Card sx={{ ...glassStyles, bgcolor: "rgba(255, 255, 255, 0.9)" }}>
            <CardContent sx={{ p: 3 }}>
              <CashOutAdminFlowDashboard />
            </CardContent>
          </Card>
        );

      case "membership-vouchers":
        return (
          <Card sx={{ ...glassStyles, bgcolor: "rgba(255, 255, 255, 0.9)" }}>
            <CardContent sx={{ p: 3 }}>
              <MembershipVoucherManagement />
            </CardContent>
          </Card>
        );

      case "ultra-plus-approvals":
        return (
          <Card sx={{ ...glassStyles, bgcolor: "rgba(255, 255, 255, 0.9)" }}>
            <CardContent sx={{ p: 3 }}>
              <UltraPlusApprovalManagement />
            </CardContent>
          </Card>
        );

      case "profit-breakdown":
        return <ProfitBreakdownView />;

      case "referrals":
        // Filter referrals based on search query
        const filteredReferrals = referrals.filter((referral) => {
          if (!referralSearchQuery.trim()) return true;
          const query = referralSearchQuery.toLowerCase();
          const referrerUser = users.find((u) => u.id === referral.referrerId);
          return (
            (referral.referrerName || "").toLowerCase().includes(query) ||
            (referral.referrerEmail || "").toLowerCase().includes(query) ||
            (referrerUser?.email || "").toLowerCase().includes(query) ||
            (referral.referredUserName || "").toLowerCase().includes(query) ||
            (referral.referredUserEmail || "").toLowerCase().includes(query) ||
            (referral.referralCode || "").toLowerCase().includes(query)
          );
        });

        return (
          <Card sx={{ ...glassStyles, bgcolor: "rgba(255, 255, 255, 0.8)" }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, borderBottom: `1px solid ${primaryBlue[200]}` }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{ color: primaryBlue[800], fontWeight: 700 }}
                    >
                      Referral Details
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: primaryBlue[600], mt: 1 }}
                    >
                      Complete referral network analysis
                    </Typography>
                  </Box>
                  <TextField
                    placeholder="Search by name, email, or referral code..."
                    value={referralSearchQuery}
                    onChange={(e) => setReferralSearchQuery(e.target.value)}
                    size="small"
                    sx={{
                      minWidth: 300,
                      bgcolor: "rgba(255, 255, 255, 0.8)",
                      borderRadius: 2,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        "&:hover fieldset": {
                          borderColor: primaryBlue[400],
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: primaryBlue[600],
                        },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: primaryBlue[500] }} />
                        </InputAdornment>
                      ),
                      endAdornment: referralSearchQuery && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setReferralSearchQuery("")}
                            sx={{ color: primaryBlue[500] }}
                          >
                            <Clear fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                {referralSearchQuery && (
                  <Typography
                    variant="body2"
                    sx={{ color: primaryBlue[600], mt: 2 }}
                  >
                    Showing {filteredReferrals.length} of {referrals.length} referrals
                  </Typography>
                )}
              </Box>
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{ fontWeight: 600, bgcolor: primaryBlue[50] }}
                      >
                        Referrer Name
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, bgcolor: primaryBlue[50] }}
                      >
                        Referrer Email
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, bgcolor: primaryBlue[50] }}
                      >
                        Points Eligible
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, bgcolor: primaryBlue[50] }}
                      >
                        Referred User
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, bgcolor: primaryBlue[50] }}
                      >
                        Referred Email
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, bgcolor: primaryBlue[50] }}
                      >
                        Referral Code
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, bgcolor: primaryBlue[50] }}
                      >
                        Date
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredReferrals.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                          <Typography variant="body1" color="text.secondary">
                            {referralSearchQuery
                              ? `No referrals found matching "${referralSearchQuery}"`
                              : "No referrals found yet."}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredReferrals.map((referral) => {
                        const referrerUser = users.find(
                          (u) => u.id === referral.referrerId
                        );
                        return (
                          <TableRow
                            key={referral.id}
                            sx={{
                              "&:hover": {
                                bgcolor: "rgba(0, 114, 206, 0.04)",
                              },
                            }}
                          >
                            <TableCell>{referral.referrerName}</TableCell>
                            <TableCell>
                              {referral.referrerEmail ||
                                referrerUser?.email ||
                                "Email not found"}
                            </TableCell>
                            <TableCell>
                              {referrerUser ? (
                                getCommissionEligibilityChip(referrerUser)
                              ) : (
                                <Chip
                                  label="Unknown"
                                  size="small"
                                  color="default"
                                />
                              )}
                            </TableCell>
                            <TableCell>{referral.referredUserName}</TableCell>
                            <TableCell>{referral.referredUserEmail}</TableCell>
                            <TableCell>
                              <Chip
                                label={referral.referralCode}
                                size="small"
                                color="primary"
                              />
                            </TableCell>
                            <TableCell>
                              {formatDate(referral.createdAt)}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        );

      case "commissions":
        return (
          <Card sx={{ ...glassStyles, bgcolor: "rgba(255, 255, 255, 0.8)" }}>
            <CardContent sx={{ p: 4 }}>
              <Typography
                variant="h5"
                sx={{ color: primaryBlue[800], fontWeight: 700, mb: 3 }}
              >
                Points Summary (Referral profit margin share)
              </Typography>

              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      ...cardGlassStyles,
                      bgcolor: "rgba(0, 114, 206, 0.1)",
                    }}
                  >
                    <CardContent sx={{ textAlign: "center" }}>
                      <Typography
                        variant="h4"
                        sx={{ color: primaryBlue[700], fontWeight: 700 }}
                      >
                        {commissions.length}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ color: primaryBlue[600], fontWeight: 600 }}
                      >
                        Total Points
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      ...cardGlassStyles,
                      bgcolor: "rgba(76, 175, 80, 0.1)",
                    }}
                  >
                    <CardContent sx={{ textAlign: "center" }}>
                      <Typography
                        variant="h4"
                        sx={{ color: "#2e7d32", fontWeight: 700 }}
                      >
                        {
                          commissions.filter((c) => c.status === "confirmed")
                            .length
                        }
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ color: "#2e7d32", fontWeight: 600 }}
                      >
                        Confirmed
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      ...cardGlassStyles,
                      bgcolor: "rgba(255, 152, 0, 0.1)",
                    }}
                  >
                    <CardContent sx={{ textAlign: "center" }}>
                      <Typography
                        variant="h4"
                        sx={{ color: "#f57c00", fontWeight: 700 }}
                      >
                        {
                          commissions.filter((c) => c.status === "pending")
                            .length
                        }
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ color: "#f57c00", fontWeight: 600 }}
                      >
                        Pending
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Alert
                severity="info"
                sx={{
                  ...glassStyles,
                  mb: 3,
                  bgcolor: "rgba(0, 114, 206, 0.1)",
                }}
              >
                <Typography variant="body2" sx={{ color: primaryBlue[700] }}>
                  Points records are only created for referrers who have
                  purchased packages. Up to 100% of profit margin can be
                  distributed across 10 levels (L1 100%, L2 25%, L3 15%, L4–L5
                  10%, L6–L8 5%, L9–L10 2.5%). Unused levels are tracked as
                  undistributed profit.
                </Typography>
              </Alert>

              <TableContainer
                sx={{
                  maxHeight: 500,
                  ...glassStyles,
                  bgcolor: "rgba(255, 255, 255, 0.9)",
                }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{ fontWeight: 600, bgcolor: primaryBlue[50] }}
                      >
                        Points ID
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, bgcolor: primaryBlue[50] }}
                      >
                        Order ID
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, bgcolor: primaryBlue[50] }}
                      >
                        Referrer
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, bgcolor: primaryBlue[50] }}
                      >
                        Level
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, bgcolor: primaryBlue[50] }}
                      >
                        Amount
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, bgcolor: primaryBlue[50] }}
                      >
                        Status
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, bgcolor: primaryBlue[50] }}
                      >
                        Date
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {commissions.slice(0, 50).map((commission) => (
                      <TableRow
                        key={commission.id}
                        sx={{
                          "&:hover": { bgcolor: "rgba(0, 114, 206, 0.04)" },
                        }}
                      >
                        <TableCell
                          sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
                        >
                          {commission.commissionId?.slice(-8) || "N/A"}
                        </TableCell>
                        <TableCell
                          sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
                        >
                          {commission.orderId?.slice(-8) || "N/A"}
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              {commission.referrerName}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {commission.referrerEmail}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`Level ${commission.commissionLevel || 1}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ color: "#00BFFF", fontWeight: 600 }}
                          >
                            +{formatPrice(commission.commissionAmount || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={commission.status || "confirmed"}
                            size="small"
                            color={
                              commission.status === "confirmed"
                                ? "success"
                                : "warning"
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {formatDate(commission.createdAt)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {commissions.length === 0 && (
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <Typography variant="body1" color="text.secondary">
                    No Points records found yet.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        );

      case "item-requests":
        return (
          <Card sx={{ ...glassStyles, bgcolor: "rgba(255, 255, 255, 0.8)" }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, pb: 1 }}>
                <Typography
                  variant="h5"
                  sx={{ color: primaryBlue[800], fontWeight: 700 }}
                >
                  Item Requests
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Manage customer requests for new items
                </Typography>
              </Box>
              
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "rgba(0, 114, 206, 0.05)" }}>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight={600}>
                          Item Name
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight={600}>
                          Requested By
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight={600}>
                          Status
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight={600}>
                          Requested Date
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight={600}>
                          Actions
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {itemRequests.map((request) => (
                      <TableRow key={request.id} hover>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: primaryBlue[700] }}
                          >
                            {request.itemName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {request.userName}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {request.userEmail}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={request.status}
                            size="small"
                            color={
                              request.status === "approved"
                                ? "success"
                                : request.status === "rejected"
                                ? "error"
                                : "warning"
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {formatDate(request.createdAt)}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            {request.status === "pending" && (
                              <>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  onClick={() => handleUpdateRequestStatus(request.id, "approved")}
                                  sx={{ minWidth: "80px" }}
                                >
                                  Approve
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={() => handleUpdateRequestStatus(request.id, "rejected")}
                                  sx={{ minWidth: "80px" }}
                                >
                                  Reject
                                </Button>
                              </>
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => handleDeleteRequest(request.id)}
                              sx={{ minWidth: "80px" }}
                            >
                              Delete
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {itemRequests.length === 0 && (
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <Typography variant="body1" color="text.secondary">
                    No item requests found yet.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        );

      default:
        return <StatisticsCards />;
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, 
          ${primaryBlue[50]} 0%, 
          ${primaryBlue[100]} 25%, 
          ${primaryBlue[200]} 50%, 
          ${primaryBlue[100]} 75%, 
          ${primaryBlue[50]} 100%)`,
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 50%, rgba(0, 114, 206, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(11, 28, 72, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 80%, rgba(0, 34, 102, 0.1) 0%, transparent 50%)
          `,
          zIndex: 0,
        },
      }}
    >
      <Box sx={{ display: "flex", position: "relative", zIndex: 1 }}>
        <NavigationDrawer />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            marginLeft: drawerOpen && !isMobile ? 0 : 0,
            transition: theme.transitions.create("margin", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          }}
        >
          {/* Mobile Header */}
          {isMobile && (
            <Box
              sx={{
                ...glassStyles,
                p: 2,
                mb: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <IconButton
                onClick={() => setDrawerOpen(!drawerOpen)}
                sx={{ color: primaryBlue[700] }}
              >
                <MenuIcon />
              </IconButton>
              <Typography
                variant="h6"
                sx={{ color: primaryBlue[800], fontWeight: 700 }}
              >
                InfinityWhale Admin
              </Typography>
              <IconButton onClick={handleMenu} sx={{ color: primaryBlue[700] }}>
                <Avatar src={user.photoURL} sx={{ width: 32, height: 32 }}>
                  <AccountCircle />
                </Avatar>
              </IconButton>
            </Box>
          )}

          {/* Desktop Header */}
          {!isMobile && (
            <Box
              sx={{
                ...glassStyles,
                p: 3,
                mb: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography
                  variant="h4"
                  sx={{ color: primaryBlue[800], fontWeight: 700, mb: 1 }}
                >
                  {navigationItems.find((item) => item.id === currentView)
                    ?.label ||
                    navigationItems
                      .find((item) =>
                        item.children?.some((child) => child.id === currentView)
                      )
                      ?.children?.find((child) => child.id === currentView)
                      ?.label ||
                    "Dashboard"}
                </Typography>
                <Typography variant="body1" sx={{ color: primaryBlue[600] }}>
                  {navigationItems.find((item) => item.id === currentView)
                    ?.description ||
                    navigationItems
                      .find((item) =>
                        item.children?.some((child) => child.id === currentView)
                      )
                      ?.children?.find((child) => child.id === currentView)
                      ?.label ||
                    "Admin control panel for InfinityWhale"}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={fetchAdminData}
                  disabled={loading}
                  sx={{
                    borderColor: primaryBlue[300],
                    color: primaryBlue[700],
                    "&:hover": {
                      borderColor: primaryBlue[500],
                      bgcolor: "rgba(0, 114, 206, 0.08)",
                    },
                  }}
                >
                  Refresh Data
                </Button>
                {currentView === "referrals" && (
                  <Button
                    variant="outlined"
                    onClick={fixReferralData}
                    disabled={loading}
                    sx={{
                      borderColor: "#D4AF37",
                      color: "#D4AF37",
                      "&:hover": {
                        borderColor: "#f57c00",
                        bgcolor: "rgba(255, 152, 0, 0.08)",
                      },
                    }}
                  >
                    Fix Referral Data
                  </Button>
                )}
              </Box>
            </Box>
          )}

          {/* Error and Success Alerts */}
          {error && (
            <Alert
              severity="error"
              sx={{ ...glassStyles, mb: 3, bgcolor: "rgba(244, 67, 54, 0.1)" }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert
              severity="success"
              sx={{ ...glassStyles, mb: 3, bgcolor: "rgba(76, 175, 80, 0.1)" }}
            >
              {success}
            </Alert>
          )}

          {/* Main Content */}
          {renderMainContent()}
        </Box>
      </Box>

      {/* User Menu for Mobile */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={onBackToHome}>
          <Home sx={{ mr: 1 }} />
          Back to Home
        </MenuItem>
        <MenuItem onClick={handleSignOut}>
          <ExitToApp sx={{ mr: 1 }} />
          Sign Out
        </MenuItem>
      </Menu>

      {/* Referral Data Cleanup Dialog */}
      <Dialog
        open={cleanupDialog}
        onClose={() => !cleanupLoading && setCleanupDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { ...glassStyles, bgcolor: "rgba(255, 255, 255, 0.95)" } }}
      >
        <DialogTitle sx={{ bgcolor: "rgba(244, 67, 54, 0.1)", borderBottom: "1px solid rgba(244, 67, 54, 0.2)" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Warning sx={{ color: "#001A4D" }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Referral Data Cleanup
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            The system has detected inconsistencies in the referral data:
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2, bgcolor: "rgba(255, 152, 0, 0.1)", borderRadius: 2, mb: 1 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#f57c00" }}>
                  Duplicate Records
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Same user has multiple referral entries
                </Typography>
              </Box>
              <Chip label={stats.duplicateReferrals} color="warning" />
            </Box>
            
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2, bgcolor: "rgba(244, 67, 54, 0.1)", borderRadius: 2 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#d32f2f" }}>
                  Orphan Records
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Referral records for non-existent users
                </Typography>
              </Box>
              <Chip label={stats.orphanReferrals} color="error" />
            </Box>
          </Box>

          <Box sx={{ p: 2, bgcolor: "rgba(0, 114, 206, 0.1)", borderRadius: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: primaryBlue[700], mb: 1 }}>
              Summary:
            </Typography>
            <Typography variant="body2">
              • Total Referral Records: <strong>{stats.totalReferrals}</strong>
            </Typography>
            <Typography variant="body2">
              • Valid Unique Referrals: <strong>{stats.validReferrals}</strong>
            </Typography>
            <Typography variant="body2">
              • Users Who Were Referred: <strong>{stats.usersWhoWereReferred}</strong>
            </Typography>
            <Typography variant="body2">
              • Users Who Made Referrals: <strong>{stats.usersWhoMadeReferrals}</strong>
            </Typography>
          </Box>

          {cleanupResults && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Cleanup completed! Removed {cleanupResults.deletedOrphans} orphan records and {cleanupResults.deletedDuplicates} duplicate records.
            </Alert>
          )}

          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Warning:</strong> This action will permanently delete the identified duplicate and orphan referral records. This cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => {
              setCleanupDialog(false);
              setCleanupResults(null);
            }}
            disabled={cleanupLoading}
          >
            Close
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCleanupReferrals}
            disabled={cleanupLoading || (stats.duplicateReferrals === 0 && stats.orphanReferrals === 0)}
            startIcon={cleanupLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {cleanupLoading ? "Cleaning up..." : `Clean Up ${stats.duplicateReferrals + stats.orphanReferrals} Records`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDashboard;
